import { Box } from '@mui/system';
import { IndentedSections } from '@openx/components-core/lib/IndentedSection/IndentedSections.tsx';
import { GeographicOption, type GeographicTargetingState } from '@openx/types/targeting/geographic.ts';
import { Criteria } from '@openx/types/targeting/targetingValuesTypes.ts';
import { noop } from '@openx/utils/noop.ts';
import { getGeoLocationDisplayName } from '@openx/utils/state/geoLocationSearch/utils.ts';
import omitBy from 'lodash/omitBy';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DimensionChipLine } from '../shared/DimensionChip/DimensionChipLine.tsx';
import { FiltersTitle } from '../shared/FiltersTitle/FiltersTitle.tsx';
import { Switch } from '../shared/Switch/Switch.tsx';
import type { TargetingItemsProps } from '../types.ts';
import { BoundingBoxItems } from './GeographicLocationItems/BoundingBoxItems.tsx';
import { CirclesItems } from './GeographicLocationItems/CircleItems.tsx';
import { ExcludeSubsetItems } from './GeographicLocationItems/ExcludeSubsetItems.tsx';
import { GeographicLocationItems } from './GeographicLocationItems/GeographicLocationItems.tsx';
import { LocationSourceItems } from './GeographicLocationItems/LocationSourceItems.tsx';
import { useGeographicItemsData } from './hooks/useGeographicItemsData.ts';
import { useHandleGeoChange } from './hooks/useHandleGeoChange.ts';
import { getEmptyLocationState } from './utils.ts';

export const GeographicItems = (props: TargetingItemsProps<GeographicTargetingState>) => {
  const { targetingParams, disabledReasonMessage, readonly = true, onChange = noop } = props;
  const { t } = useTranslation();
  const {
    excludes,
    includes,
    includesKeys,
    excludesKeys,
    locationCount,
    locationsSwitchState,
    isCirclesOptionSelected,
    isLocationSourceSelected,
    isBoundingBoxEmpty,
  } = useGeographicItemsData(targetingParams);

  const { handleChange, onLocationRemove, onLocationClear } = useHandleGeoChange(
    targetingParams,
    onChange,
    locationsSwitchState
  );

  const handleLocationSwitchChange = useCallback(() => {
    onChange({
      ...targetingParams,
      [GeographicOption.INCLUDES]: locationsSwitchState
        ? { ...targetingParams[GeographicOption.EXCLUDES] }
        : getEmptyLocationState(),
      [GeographicOption.EXCLUDES_SUBSET]: locationsSwitchState
        ? [...targetingParams[GeographicOption.EXCLUDES_SUBSET]]
        : [],
      [GeographicOption.EXCLUDES]: locationsSwitchState
        ? getEmptyLocationState()
        : { ...targetingParams[GeographicOption.INCLUDES] },
    });
  }, [onChange, locationsSwitchState, targetingParams]);

  const geographicInventoryOptions = {
    [GeographicOption.INCLUDES]: locationCount
      ? (isNotLastItem: boolean) => (
          <div data-test="sub-section">
            <FiltersTitle
              onClear={onLocationClear}
              title={t('Location [ {count} ]', { count: locationCount })}
              readonly={readonly}
              disabledReasonMessage={disabledReasonMessage}
            />
            {!readonly && (
              <Switch
                switchState={locationsSwitchState}
                onSwitchChange={handleLocationSwitchChange}
                switchOffLabel={t('include')}
                switchOnLabel={t('exclude')}
                groupTitle={t('the following:')}
              />
            )}

            {locationsSwitchState ? (
              <GeographicLocationItems
                locationKeys={excludesKeys}
                locations={excludes}
                readonly={readonly}
                handleChange={handleChange}
                onLocationRemove={onLocationRemove}
                locationsSwitchState={false}
                getGeoLocationDisplayName={getGeoLocationDisplayName}
              />
            ) : (
              <GeographicLocationItems
                locationKeys={includesKeys}
                locations={includes}
                readonly={readonly}
                handleChange={handleChange}
                onLocationRemove={onLocationRemove}
                locationsSwitchState={true}
                getGeoLocationDisplayName={getGeoLocationDisplayName}
              />
            )}

            <ExcludeSubsetItems {...props} />
            {isNotLastItem && <DimensionChipLine targetingDimension={Criteria.ANY} />}
          </div>
        )
      : null,

    [GeographicOption.CIRCLES]: isCirclesOptionSelected
      ? (isNoLastItem: boolean) => (
          <CirclesItems
            isNotLastItem={isNoLastItem}
            targetingParams={targetingParams}
            readonly={readonly}
            onChange={onChange}
          />
        )
      : null,

    [GeographicOption.BOUNDING_BOX]: !isBoundingBoxEmpty
      ? () => <BoundingBoxItems targetingParams={targetingParams} readonly={readonly} onChange={onChange} />
      : null,
  };

  const selectedInventories = Object.keys(omitBy(geographicInventoryOptions, item => item === null));

  if (isLocationSourceSelected) {
    return (
      <>
        {selectedInventories.length !== 0 && (
          <>
            <IndentedSections>
              {selectedInventories.map((inventory, index) => (
                <Box key={`k-${index}`}>
                  {inventory ? geographicInventoryOptions[inventory](selectedInventories.length - 1 > index) : null}
                </Box>
              ))}
            </IndentedSections>
            <DimensionChipLine targetingDimension={Criteria.ALL} />
          </>
        )}
        <LocationSourceItems targetingParams={targetingParams} readonly={readonly} onChange={onChange} />
      </>
    );
  }

  return (
    <>
      {selectedInventories.map((inventory, index) => (
        <Box key={`k-${index}`}>
          {inventory ? geographicInventoryOptions[inventory](selectedInventories.length - 1 > index) : null}
        </Box>
      ))}
    </>
  );
};
